import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, Typography, TablePagination, TextField, InputAdornment, Chip, alpha, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import PaidIcon from '@mui/icons-material/Paid';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { Invoice, InvoiceStatus } from '../../types/b2b';

const fetchInvoices = async () => {
  const response = await axiosInstance.get('/invoices');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const filterOptions: FilterOption[] = [
  { field: 'status', label: 'Status', type: 'select', options: [
    { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' }, { value: 'paid', label: 'Paid' },
    { value: 'partially_paid', label: 'Partially Paid' }, { value: 'overdue', label: 'Overdue' }, { value: 'canceled', label: 'Canceled' },
  ]},
];

const exportColumns: ColumnConfig[] = [
  { field: 'invoiceNumber', headerName: 'Invoice #' },
  { field: 'customer', headerName: 'Customer', valueFormatter: (_, row) => row.customer ? `${row.customer.firstName} ${row.customer.lastName}` : 'N/A' },
  { field: 'status', headerName: 'Status' },
  { field: 'total', headerName: 'Total', valueFormatter: (value) => `${Number(value).toLocaleString()} DZD` },
  { field: 'amountDue', headerName: 'Amount Due', valueFormatter: (value) => `${Number(value).toLocaleString()} DZD` },
  { field: 'dueDate', headerName: 'Due Date', valueFormatter: (value) => new Date(value).toLocaleDateString() },
];

const getStatusColor = (status: InvoiceStatus): string => {
  switch (status) {
    case 'draft': return palette.textMuted;
    case 'sent': return palette.info;
    case 'paid': return palette.success;
    case 'partially_paid': return palette.warning;
    case 'overdue': return palette.error;
    case 'canceled': return palette.textMuted;
    case 'refunded': return palette.info;
    default: return palette.textMuted;
  }
};

export const InvoiceList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { data: invoices, isLoading } = useQuery({ queryKey: ['invoices'], queryFn: fetchInvoices });

  const sendMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.post(`/invoices/${id}/send`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['invoices'] }); showMessage('Invoice sent successfully', 'success'); },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to send invoice', 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.post(`/invoices/${id}/cancel`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['invoices'] }); showMessage('Invoice canceled', 'success'); },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to cancel invoice', 'error'),
  });

  const filteredData = useMemo(() => {
    if (!invoices) return [];
    return invoices.filter((item: Invoice) => {
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const customerName = item.customer ? `${item.customer.firstName} ${item.customer.lastName}` : '';
        if (!item.invoiceNumber.toLowerCase().includes(search) && !customerName.toLowerCase().includes(search)) return false;
      }
      if (activeFilters.status && item.status !== activeFilters.status) return false;
      return true;
    });
  }, [invoices, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const stats = useMemo(() => {
    const total = filteredData.length;
    const paid = filteredData.filter((i: Invoice) => i.status === 'paid').length;
    const pending = filteredData.filter((i: Invoice) => ['draft', 'sent', 'partially_paid'].includes(i.status)).length;
    const overdue = filteredData.filter((i: Invoice) => i.status === 'overdue').length;
    const totalRevenue = filteredData.filter((i: Invoice) => i.status === 'paid').reduce((sum: number, i: Invoice) => sum + (i.total || 0), 0);
    return { total, paid, pending, overdue, totalRevenue };
  }, [filteredData]);

  const handleMenuClose = () => { setAnchorEl(null); setSelectedInvoice(null); };

  const handleDownloadPdf = async () => {
    if (selectedInvoice) {
      try {
        const response = await axiosInstance.get(`/invoices/${selectedInvoice.id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-${selectedInvoice.invoiceNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        showMessage('PDF downloaded successfully', 'success');
      } catch { showMessage('Failed to download PDF', 'error'); }
    }
    handleMenuClose();
  };

  const colors = mode === 'dark' ? { surface: palette.surface, surfaceElevated: palette.surfaceElevated, border: palette.border, borderLight: palette.borderLight, textPrimary: palette.textPrimary, textMuted: palette.textMuted } : { surface: palette.light.surface, surfaceElevated: palette.light.surfaceElevated, border: palette.light.border, borderLight: palette.light.borderLight, textPrimary: palette.light.textPrimary, textMuted: palette.light.textMuted };
  const formatCurrency = (amount: number) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' });

  const getStatusColorType = (status: InvoiceStatus): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'partially_paid': return 'warning';
      case 'sent': return 'info';
      default: return 'default';
    }
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<Invoice>[] = [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice #',
      render: (value, row) => {
        const isOverdue = row.status === 'overdue';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ fontSize: 18, color: isOverdue ? palette.error : palette.primary }} />
            <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold }}>
              {value}
            </Typography>
            {isOverdue && <WarningIcon sx={{ fontSize: 16, color: palette.error }} />}
          </Box>
        );
      },
    },
    {
      field: 'customer',
      headerName: 'Customer',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: palette.info }} />
          <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: colors.textPrimary, fontSize: typography.fontSize.sm }}>
            {row.customer ? `${row.customer.firstName} ${row.customer.lastName}` : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      render: (value) => {
        const statusColor = getStatusColor(value);
        return (
          <Chip
            label={value.replace('_', ' ').toUpperCase()}
            sx={{
              backgroundColor: alpha(statusColor, 0.1),
              color: statusColor,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${statusColor}`,
            }}
          />
        );
      },
    },
    {
      field: 'amountDue',
      headerName: 'Amount Due',
      render: (value) => {
        const color = value > 0 ? palette.warning : palette.success;
        return (
          <Box sx={{ display: 'inline-flex', px: 2, py: 0.75, borderRadius: theme.radius.xs, backgroundColor: alpha(color, 0.1), border: `1px solid ${color}` }}>
            <Typography sx={{ fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.extrabold, color }}>
              {formatCurrency(value || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      render: (value, row) => {
        const isOverdue = row.status === 'overdue';
        return (
          <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.semibold, color: isOverdue ? palette.error : colors.textPrimary, fontSize: typography.fontSize.sm }}>
            {formatDate(value)}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ReceiptIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography variant="overline" sx={{ color: palette.primary, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>B2B BILLING SYSTEM</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, letterSpacing: '-0.02em', lineHeight: 1.1 }}>INVOICES</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/app/invoices/create')} sx={{ backgroundColor: palette.primary, color: '#FFFFFF', fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', fontSize: typography.fontSize.sm, px: 3, py: 1.5, borderRadius: theme.radius.sm, '&:hover': { backgroundColor: alpha(palette.primary, 0.8), transform: 'translateY(-2px) scale(1.02)' } }}>New Invoice</Button>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.primary } }}><Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>TOTAL</Typography><Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, mt: 1 }}>{stats.total}</Typography></Card>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.success } }}><Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>PAID</Typography><Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.success, mt: 1 }}>{stats.paid}</Typography></Card>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.warning } }}><Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>PENDING</Typography><Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.warning, mt: 1 }}>{stats.pending}</Typography></Card>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.error } }}><Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>OVERDUE</Typography><Typography variant="h3" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.error, mt: 1 }}>{stats.overdue}</Typography></Card>
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2.5, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: palette.success } }}><Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>REVENUE</Typography><Typography variant="h5" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.success, mt: 1 }}>{formatCurrency(stats.totalRevenue)}</Typography></Card>
      </Box>

      {/* Search */}
      <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 2, mb: 3, ...animations.slideUp(0.15) }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField placeholder="SEARCH INVOICES..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} sx={{ flex: 1, minWidth: 300, '& .MuiOutlinedInput-root': { backgroundColor: colors.surfaceElevated, border: `1px solid ${colors.border}`, borderRadius: theme.radius.sm, '& fieldset': { border: 'none' } }, '& .MuiInputBase-input': { color: colors.textPrimary, textTransform: 'uppercase' } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.textMuted }} /></InputAdornment> }} />
          <TableToolbar title="" searchValue="" onSearchChange={() => {}} searchPlaceholder="" filters={filterOptions} activeFilters={activeFilters} onFilterChange={(f, v) => setActiveFilters((p) => ({ ...p, [f]: v }))} onClearFilters={() => setActiveFilters({})} data={filteredData} columns={exportColumns} exportFileName="invoices" />
        </Box>
      </Card>

      {/* Table */}
      <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, overflow: 'hidden', ...animations.slideUp(0.2) }}>
        <ResponsiveTable
          columns={tableColumns}
          rows={paginatedData}
          loading={isLoading}
          emptyMessage="No invoices found"
          primaryField="invoiceNumber"
          getPrimaryText={(row) => row.invoiceNumber || `Invoice #${row.id?.slice(0, 8) || 'Unknown'}`}
          statusField="status"
          getStatusColor={(status) => getStatusColorType(status as InvoiceStatus)}
          onRowClick={(invoice) => navigate(`/app/invoices/show/${invoice.id}`)}
        />
        <Box sx={{ backgroundColor: colors.surfaceElevated, borderTop: `2px solid ${colors.border}` }}>
          <TablePagination component="div" count={filteredData.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={pageSize} onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} sx={{ '& .MuiTablePagination-toolbar': { color: colors.textPrimary } }} />
        </Box>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { backgroundColor: colors.surface, border: `1px solid ${colors.border}` } }}>
        {selectedInvoice?.status === 'draft' && <MenuItem onClick={() => { sendMutation.mutate(selectedInvoice.id); handleMenuClose(); }}><ListItemIcon><SendIcon fontSize="small" sx={{ color: palette.info }} /></ListItemIcon><ListItemText primary="Send" sx={{ color: colors.textPrimary }} /></MenuItem>}
        {['sent', 'partially_paid', 'overdue'].includes(selectedInvoice?.status || '') && <MenuItem onClick={() => { navigate(`/app/invoices/show/${selectedInvoice?.id}?action=pay`); handleMenuClose(); }}><ListItemIcon><PaidIcon fontSize="small" sx={{ color: palette.success }} /></ListItemIcon><ListItemText primary="Record Payment" sx={{ color: colors.textPrimary }} /></MenuItem>}
        <MenuItem onClick={handleDownloadPdf}><ListItemIcon><PictureAsPdfIcon fontSize="small" sx={{ color: palette.error }} /></ListItemIcon><ListItemText primary="Download PDF" sx={{ color: colors.textPrimary }} /></MenuItem>
        {['draft', 'sent'].includes(selectedInvoice?.status || '') && <MenuItem onClick={() => { if (window.confirm('Cancel this invoice?')) { cancelMutation.mutate(selectedInvoice!.id); } handleMenuClose(); }}><ListItemIcon><CancelIcon fontSize="small" sx={{ color: palette.error }} /></ListItemIcon><ListItemText primary="Cancel" sx={{ color: palette.error }} /></MenuItem>}
      </Menu>
    </Box>
  );
};
